import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Seo } from "@/components/seo/Seo";
import { SERVICES, CITIES } from "../data/landingConfig";
import { ArrowRight, Building2, Check, MapPin } from "lucide-react";

export default function LandingServizio() {
  const navigate = useNavigate();
  const { servizio, service: serviceParam, città, city: cityParam } = useParams<{
    servizio?: string;
    service?: string;
    città?: string;
    city?: string;
  }>();

  const service = SERVICES[servizio ?? serviceParam ?? ""];
  const city = CITIES[città ?? cityParam ?? ""];

  if (!service || !city) {
    return (
      <main className="container mx-auto px-6 py-16 text-center">
        <Seo title="Servizio non trovato | Halomora" canonicalPath="/" />
        <h1 className="text-2xl font-bold text-gray-900">Servizio non trovato</h1>
        <p className="mt-2 text-gray-600">
          Torna alla <Link to="/" className="text-primary hover:underline">homepage</Link> per scoprire tutti i servizi.
        </p>
      </main>
    );
  }

  const cityName = city.nomeEsteso;
  const canonicalPath = `/servizi/${service.slug}/${city.slug}`;
  const localBlurb = `${cityName} significa ${city.contestoLocale}. ${city.specificita}: le imprese su Halomora organizzano gli interventi di conseguenza e ti inviano preventivi gratuiti entro 24 ore.`;

  const otherServices = Object.values(SERVICES).filter((s) => s.slug !== service.slug).slice(0, 5);
  const otherCities = Object.values(CITIES).filter((c) => c.slug !== city.slug);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: `${service.nome} a ${cityName}`,
      provider: { "@type": "Organization", name: "Halomora", url: "https://halomora.com" },
      areaServed: { "@type": "City", name: cityName },
      serviceType: service.nome,
      url: `https://halomora.com${canonicalPath}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: service.faq.map((f) => ({
        "@type": "Question",
        name: f.domanda,
        acceptedAnswer: { "@type": "Answer", text: f.risposta },
      })),
    },
  ];

  return (
    <div className="min-h-screen app-page">
      <Seo title={service.metaTitle(cityName)} description={service.metaDescription(cityName)} canonicalPath={canonicalPath} jsonLd={jsonLd} />

      <section className="relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.28),transparent_34%),radial-gradient(circle_at_top_right,rgba(16,185,129,0.18),transparent_30%),linear-gradient(180deg,rgba(15,23,42,0.95),rgba(2,6,23,0.98))]" />
        <div className="relative container mx-auto px-6 py-14 md:py-20">
          <div className="max-w-3xl">
            <Badge className="border border-white/15 bg-white text-primary hover:bg-white">{service.nome}</Badge>
            <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight">
              {service.heroTitle(cityName)}
            </h1>
            <p className="mt-5 text-lg md:text-xl text-slate-200 leading-relaxed">{service.heroParagraph(cityName)}</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button size="lg" className="shadow-xl" onClick={() => navigate("/")}>Richiedi un preventivo gratuito</Button>
              <Button size="lg" variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/12" onClick={() => navigate("/")}>Trova un professionista</Button>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-12 md:py-16 space-y-16">
        <section className="grid gap-6 lg:grid-cols-2 lg:items-center">
          <div className="space-y-4">
            <Badge variant="outline"><MapPin className="h-3.5 w-3.5 mr-1 inline" /> {cityName} e dintorni</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">Un servizio pensato per {cityName}</h2>
            <p className="text-gray-600 leading-relaxed">{localBlurb}</p>
            <p className="text-sm text-gray-600"><strong>Zone coperte:</strong> {city.zoneVicine}.</p>
          </div>
          <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-emerald-500/5 shadow-card">
            <CardContent className="p-6 space-y-3">
              {["Preventivi gratuiti entro 24 ore", "Imprese verificate con P.IVA e assicurazione", "Confronti le offerte e scegli", "Prenoti e gestisci tutto online"].map((line) => (
                <div key={line} className="flex items-start gap-3 text-sm text-gray-700">
                  <Check className="h-4 w-4 shrink-0 mt-0.5 text-primary" strokeWidth={2.5} />
                  <span>{line}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>

        <section className="space-y-6">
          <div className="max-w-3xl">
            <Badge variant="outline" className="mb-3">Cosa è incluso</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">I servizi principali</h2>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            {service.cards.map((card) => (
              <Card key={card.titolo} className="border-gray-200 shadow-card h-full">
                <CardHeader>
                  <CardTitle className="text-lg">{card.titolo}</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-gray-600 leading-relaxed">{card.testo}</CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="space-y-6">
          <div className="max-w-3xl">
            <Badge variant="outline" className="mb-3">Semplice</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">Come funziona</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {[
              ["Descrivi la tua esigenza", "Inserisci indirizzo, tipo di servizio e tempi."],
              ["Ricevi preventivi gratuiti", "Le imprese locali verificate ti contattano entro 24 ore."],
              ["Scegli e prenota", "Confronti condizioni e tariffe e confermi online."],
            ].map(([t, d], i) => (
              <div key={t} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white font-semibold text-sm">{i + 1}</div>
                <h3 className="mt-4 text-lg font-semibold text-gray-900">{t}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="relative overflow-hidden rounded-[1.75rem] border border-primary/20 bg-gradient-to-br from-primary/5 to-emerald-500/5">
          <div className="px-6 py-8 md:flex md:items-center md:justify-between md:gap-6">
            <div className="flex items-start gap-4">
              <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Building2 className="h-5 w-5" /></div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{`Sei un'impresa di pulizie a ${cityName}?`}</h2>
                <p className="mt-1 text-sm text-gray-600">Ricevi richieste come questa, un sito professionale e un gestionale. Gratis per iniziare.</p>
              </div>
            </div>
            <Button className="mt-4 md:mt-0 shrink-0" onClick={() => navigate(`/aziende/pulizie/${city.slug}`)}>
              Lavora con noi a {cityName} <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">Domande frequenti</h2>
          <div className="space-y-3">
            {service.faq.map((item) => (
              <details key={item.domanda} className="rounded-lg border border-gray-200 p-4 bg-white">
                <summary className="cursor-pointer font-medium text-gray-900">{item.domanda}</summary>
                <p className="mt-2 text-gray-600 leading-relaxed text-sm">{item.risposta}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="grid gap-8 md:grid-cols-2">
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Altri servizi a {cityName}</h2>
            <div className="flex flex-wrap gap-2">
              {otherServices.map((s) => (
                <Link key={s.slug} to={`/servizi/${s.slug}/${city.slug}`} className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-medium text-slate-700 hover:border-primary/40 hover:text-primary">
                  {s.nome}
                </Link>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">{service.nome} in altre città</h2>
            <div className="flex flex-wrap gap-2">
              {otherCities.map((c) => (
                <Link key={c.slug} to={`/servizi/${service.slug}/${c.slug}`} className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-medium text-slate-700 hover:border-primary/40 hover:text-primary">
                  {c.nomeEsteso}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
